import { createContext, useContext } from 'react';
import type { UserRole } from '@tailoring-crm/shared';
import type { AuthShop, AuthUser, ShopChoice } from './auth-contract.js';

/**
 * Who is signed in, as the whole application sees it.
 *
 * Three states and one flag, and the flag is the interesting one:
 *
 *  - `loading`       the app has just booted and is asking the server. Nothing is rendered
 *                    yet, because flashing the login screen at somebody who IS signed in is
 *                    the single most common bug in a cookie-authenticated SPA;
 *  - `authenticated` there is a session;
 *  - `anonymous`     there is not, and the auth screens are what should be on screen.
 *
 *  - `expired`       there WAS a session and it has just died under someone's fingers. The
 *                    status deliberately stays `authenticated`: the screen they were working
 *                    on stays mounted, with whatever they had typed still in it, and a modal
 *                    asks for the password. Redirecting to `/login` here would be correct and
 *                    would also throw away a half-finished order.
 */
export interface AuthSnapshot {
  user: AuthUser;
  shop: AuthShop | null;
  role: UserRole;
  sessionId: string;
  /** Every shop this person belongs to — the switcher needs no second call. */
  memberships: ShopChoice[];
}

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

export interface AuthState {
  status: AuthStatus;
  user: AuthUser | null;
  shop: AuthShop | null;
  role: UserRole | null;
  sessionId: string | null;
  memberships: ShopChoice[];
  expired: boolean;
}

export interface AuthApi extends AuthState {
  /** Adopt the session a credential endpoint just issued. */
  signIn: (snapshot: AuthSnapshot) => void;
  signOut: () => Promise<void>;
  /** Re-read `/auth/me`, after something changed the membership or the shop. */
  reload: () => Promise<void>;
}

export const AuthContext = createContext<AuthApi | null>(null);

export function useAuth(): AuthApi {
  const auth = useContext(AuthContext);
  if (!auth) throw new Error('useAuth must be used inside <AuthProvider>');
  return auth;
}

/** For the few places that render both inside and outside the provider (the error boundary). */
export function useOptionalAuth(): AuthApi | null {
  return useContext(AuthContext);
}

export const ANONYMOUS: AuthState = Object.freeze({
  status: 'anonymous',
  user: null,
  shop: null,
  role: null,
  sessionId: null,
  memberships: [],
  expired: false,
});
